import {
    DAuditInfo,
    IAuditStamps,
    RhCheckbox,
    RhFormWrapper,
    RhNumberField,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import { AccountContactsColumns } from './account-contacts/account-contacts-columns';
import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { SchemaOf, ValidationError } from 'yup';
import {
    ActivityEntityName,
    useSimilarAccountNamesLazyQuery,
    useUpsertAccountDialogMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import {
    FIELD_LABELS,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import { RequiresOrderRequest } from './requires-order-request/requires-order-request';
import { ResourceIdInput } from './resource-id/resource-id-input';
import {
    AccountProductsColumns,
    ACCOUNT_PRODUCTS,
    IAccountProductRow,
} from '../account-products/account-products-columns';
import {
    AccountResourcesColumns,
    ACCOUNT_RESOURCES,
    IAccountResourceRow,
} from '../account-resources/account-resources-columns';
import {
    SimilarAccountName,
    SimilarAccountNameWarning,
} from './similar-account-name-warning';
import { FormFieldSection } from '../../form-field-section';
import { useFormContext, useWatch } from 'react-hook-form';

export interface IAccount {
    id?: number | null;
    name: string;
    abbreviation?: string;
    // General identity, printed on the cotización's DATOS DEL CLIENTE block.
    rfc?: string;
    address?: string;
    is_supplier: boolean;
    requires_order_request: boolean;
    monitor_supplier_expenses: boolean;
    resource_id?: number | null;
    is_client: boolean;
    // Credit terms + default flags. client_* pre-fill order sales, supplier_*
    // pre-fill expenses, when this account is selected on those forms.
    client_credit_days: number;
    supplier_credit_days: number;
    client_require_credit_note: boolean;
    client_require_supplement: boolean;
    client_requires_invoice_code: boolean;
    client_requires_tax: boolean;
    supplier_require_supplement: boolean;
    supplier_requires_external_code: boolean;
    supplier_requires_tax: boolean;
    supplier_recurring_expenses: boolean;
    client_automatic_tax_calculation: boolean;
    client_reconciliation_only: boolean;
    supplier_reconciliation_only: boolean;
    supplier_is_draft: boolean;
    is_informal_account: boolean;
    exclude_from_balances_summary: boolean;
    account_contacts: {
        id?: number | null;
        first_name: string;
        last_name: string;
        cellphone?: string | null;
        email?: string | null;
    }[];
    account_products: IAccountProductRow[];
    account_resources: IAccountResourceRow[];
}

type ProductOption = {
    id: number;
    compound_description: string;
    current_group_weight?: number | null;
};

type ResourceOption = {
    id: number;
    name: string;
};

interface IAccountForm {
    // The catalogs arrive via separate query fields (`accountProducts` /
    // `accountResources`), not on the account object, so they're omitted from
    // the account prop's type.
    // Audit stamps ride alongside the form values — display only, never
    // submitted, and deliberately kept out of IAccount (see IAuditStamps).
    account?:
        | (Omit<IAccount, 'account_products' | 'account_resources'> &
              IAuditStamps)
        | null;
    accountProducts?: IAccountProductRow[];
    accountResources?: IAccountResourceRow[];
    products: ProductOption[];
    resources: ResourceOption[];
    onSuccess: () => void;
    disabled?: boolean;
}

export const IS_SUPPLIER: keyof IAccount = 'is_supplier';
export const IS_CLIENT: keyof IAccount = 'is_client';
export const MONITOR_SUPPLIER_EXPENSES: keyof IAccount =
    'monitor_supplier_expenses';
export const IS_INFORMAL_ACCOUNT: keyof IAccount = 'is_informal_account';
export const EXCLUDE_FROM_BALANCES_SUMMARY: keyof IAccount =
    'exclude_from_balances_summary';

function SupplierDraftDefault() {
    const monitorSupplierExpenses = useWatch({
        name: MONITOR_SUPPLIER_EXPENSES,
    }) as IAccount['monitor_supplier_expenses'];
    const { setValue } = useFormContext<IAccount>();

    useEffect(() => {
        if (monitorSupplierExpenses) {
            setValue('supplier_is_draft', true, { shouldValidate: true });
        }
    }, [monitorSupplierExpenses, setValue]);

    return (
        <RhCheckbox
            name={'supplier_is_draft'}
            label={shortLabel(FIELD_LABELS.supplier_is_draft)}
            disabled={monitorSupplierExpenses}
        />
    );
}

// A product can appear only once in an account's catalog.
function isProductUnique(value: any, context: any): boolean | ValidationError {
    const { product } = context.from[0].value as IAccountProductRow;
    if (!product) return true;

    const accountProducts = (context.from[1].value as IAccount)
        .account_products;
    const matches = accountProducts.filter(
        (ap) => ap.product && ap.product.id === product.id,
    );
    if (matches.length > 1) {
        return context.createError({ message: `El producto no es unico` });
    }
    return true;
}

// At most one of kilo price / group price may be non-zero. Both at 0 is allowed
// (a free catalog product); order requests for it must then carry 0 too.
function isAtMostOnePriceNonZero(
    _value: any,
    context: any,
): boolean | ValidationError {
    const { kilo_price, group_price } = context.from[0]
        .value as IAccountProductRow;
    if (Number(kilo_price) !== 0 && Number(group_price) !== 0) {
        return context.createError({
            message: `Solo uno de los precios puede ser distinto de 0`,
        });
    }
    return true;
}

export function AccountForm({
    account,
    accountProducts,
    accountResources,
    products,
    resources,
    onSuccess,
    disabled = false,
}: IAccountForm) {
    const [mutation, { loading }] = useUpsertAccountDialogMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.Account,
        entityId: account?.id,
        unsavedMessage:
            'El historial aparece una vez que la cuenta ha sido guardada.',
        emptyMessage: 'No hay actividades registradas para esta cuenta.',
    });
    const [getSimilarAccountNames] = useSimilarAccountNamesLazyQuery({
        fetchPolicy: 'network-only',
    });
    const [name, setName] = useState(account?.name || '');
    const [similarNameMatches, setSimilarNameMatches] = useState<
        SimilarAccountName[]
    >([]);

    useEffect(() => {
        setName(account?.name || '');
        setSimilarNameMatches([]);
    }, [account?.id, account?.name]);

    useEffect(() => {
        const trimmedName = name.trim();
        if (!trimmedName || name === account?.name) {
            setSimilarNameMatches([]);
            return;
        }

        let cancelled = false;
        const timeout = window.setTimeout(async () => {
            try {
                const result = await getSimilarAccountNames({
                    variables: {
                        Name: trimmedName,
                        ExcludeAccountId: account?.id || null,
                    },
                });
                if (!cancelled) {
                    setSimilarNameMatches(
                        result.data?.getSimilarAccountNames || [],
                    );
                }
            } catch (error) {
                if (!cancelled) setSimilarNameMatches([]);
                console.error(error);
            }
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [account?.id, account?.name, getSimilarAccountNames, name]);

    const initialValues = useMemo<IAccount>(() => {
        return {
            id: account?.id || undefined,
            name: account?.name || '',
            abbreviation: account?.abbreviation || '',
            rfc: account?.rfc || '',
            address: account?.address || '',
            is_client: account ? account.is_client : false,
            is_supplier: account ? account.is_supplier : false,
            requires_order_request: account
                ? account.requires_order_request
                : true,
            monitor_supplier_expenses: account
                ? account.monitor_supplier_expenses
                : false,
            client_credit_days: account?.client_credit_days ?? 0,
            supplier_credit_days: account?.supplier_credit_days ?? 0,
            client_require_credit_note:
                account?.client_require_credit_note ?? false,
            client_require_supplement:
                account?.client_require_supplement ?? true,
            client_requires_invoice_code:
                account?.client_requires_invoice_code ?? true,
            client_requires_tax: account?.client_requires_tax ?? true,
            supplier_require_supplement:
                account?.supplier_require_supplement ?? false,
            supplier_requires_external_code:
                account?.supplier_requires_external_code ?? true,
            supplier_requires_tax: account?.supplier_requires_tax ?? true,
            supplier_recurring_expenses:
                account?.supplier_recurring_expenses ?? false,
            client_automatic_tax_calculation:
                account?.client_automatic_tax_calculation ?? true,
            client_reconciliation_only:
                account?.client_reconciliation_only ?? false,
            supplier_reconciliation_only:
                account?.supplier_reconciliation_only ?? false,
            // Monitored suppliers are backfilled to draft-by-default; other
            // suppliers can opt into the same behavior.
            supplier_is_draft: account?.supplier_is_draft ?? false,
            is_informal_account: account?.is_informal_account ?? false,
            exclude_from_balances_summary:
                account?.exclude_from_balances_summary ?? false,
            account_contacts: account?.account_contacts || [],
            account_products: accountProducts || [],
            account_resources: accountResources || [],
            resource_id: account?.resource_id || null,
        };
    }, [account, accountProducts, accountResources]);

    const validationSchema: SchemaOf<IAccount> = yup.object({
        id: yup.number().notRequired().nullable(),
        name: yup.string().required(`'Nombre' es obligatorio`),
        abbreviation: yup.string(),
        rfc: yup.string(),
        address: yup.string(),
        is_client: yup.boolean().required(),
        is_supplier: yup.boolean().required(),
        requires_order_request: yup.boolean().required(),
        monitor_supplier_expenses: yup.boolean().required(),
        client_credit_days: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(),
        supplier_credit_days: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(),
        client_require_credit_note: yup.boolean().required(),
        client_require_supplement: yup.boolean().required(),
        client_requires_invoice_code: yup.boolean().required(),
        client_requires_tax: yup.boolean().required(),
        supplier_require_supplement: yup.boolean().required(),
        supplier_requires_external_code: yup.boolean().required(),
        supplier_requires_tax: yup.boolean().required(),
        supplier_recurring_expenses: yup.boolean().required(),
        client_automatic_tax_calculation: yup.boolean().required(),
        client_reconciliation_only: yup.boolean().required(),
        supplier_reconciliation_only: yup.boolean().required(),
        supplier_is_draft: yup.boolean().required(),
        is_informal_account: yup.boolean().required(),
        exclude_from_balances_summary: yup.boolean().required(),
        resource_id: yup
            .number()
            .transform(transformToNumber)
            .notRequired()
            .nullable(),
        account_contacts: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    first_name: yup
                        .string()
                        .required(`'Nombre' es obligatorio`),
                    last_name: yup
                        .string()
                        .required(`'Apellidos' es obligatorio`),
                    cellphone: yup.string().notRequired().nullable(),
                    email: yup.string().notRequired().nullable(),
                }),
            )
            .required('`Contactos` es obligatorio'),
        account_products: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    product: yup
                        .mixed()
                        .required(`'Producto' es obligatorio`)
                        .nullable()
                        .test('is-product-unique', isProductUnique),
                    kilo_price: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Precio por kilo' es requerido`)
                        .test(
                            'is-at-most-one-price-non-zero',
                            isAtMostOnePriceNonZero,
                        ),
                    group_price: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Precio por bulto' es requerido`)
                        .test(
                            'is-at-most-one-price-non-zero',
                            isAtMostOnePriceNonZero,
                        ),
                    group_weight: yup
                        .number()
                        .transform(transformToNumber)
                        .notRequired()
                        .nullable(),
                }),
            )
            .required(),
        // Supplier resource catalog. Guideline-only: a resource is required per
        // row, but duplicates are allowed (no uniqueness test) and any unit
        // price — including 0 — is valid.
        account_resources: yup
            .array()
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    resource: yup
                        .mixed()
                        .required(`'Recurso' es obligatorio`)
                        .nullable(),
                    unit_price: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'$ unitario' es requerido`),
                    notes: yup.string(),
                }),
            )
            .required(),
    });

    const submitAccount = async (data: IAccount) => {
        try {
            await mutation({
                variables: {
                    AccountUpsertInput: {
                        id: account?.id || null,
                        name: data.name,
                        abbreviation: data.abbreviation || '',
                        rfc: data.rfc || '',
                        address: data.address || '',
                        is_client: data ? data.is_client : false,
                        requires_order_request: data
                            ? data.requires_order_request
                            : false,
                        monitor_supplier_expenses: data
                            ? data.monitor_supplier_expenses
                            : false,
                        client_credit_days: data.client_credit_days,
                        supplier_credit_days: data.supplier_credit_days,
                        client_require_credit_note:
                            data.client_require_credit_note,
                        client_require_supplement:
                            data.client_require_supplement,
                        client_requires_invoice_code:
                            data.client_requires_invoice_code,
                        client_requires_tax: data.client_requires_tax,
                        supplier_require_supplement:
                            data.supplier_require_supplement,
                        supplier_requires_external_code:
                            data.supplier_requires_external_code,
                        supplier_requires_tax: data.supplier_requires_tax,
                        supplier_recurring_expenses:
                            data.supplier_recurring_expenses,
                        client_automatic_tax_calculation:
                            data.client_automatic_tax_calculation,
                        client_reconciliation_only:
                            data.client_reconciliation_only,
                        supplier_reconciliation_only:
                            data.supplier_reconciliation_only,
                        supplier_is_draft:
                            data.monitor_supplier_expenses ||
                            data.supplier_is_draft,
                        is_supplier: data ? data.is_supplier : false,
                        resource_id: data ? data.resource_id : null,
                        account_contacts: data.account_contacts.map(
                            (contact) => {
                                return {
                                    id: contact.id || null,
                                    first_name: contact.first_name,
                                    last_name: contact.last_name,
                                    cellphone: contact.cellphone!,
                                    email: contact.email!,
                                };
                            },
                        ),
                        account_products: data.account_products.map(
                            (accountProduct) => {
                                return {
                                    id: accountProduct.id || null,
                                    product_id: accountProduct.product!.id,
                                    kilo_price: accountProduct.kilo_price,
                                    group_price: accountProduct.group_price,
                                    group_weight:
                                        accountProduct.group_weight || 0,
                                };
                            },
                        ),
                        account_resources: data.account_resources.map(
                            (accountResource) => {
                                return {
                                    id: accountResource.id || null,
                                    resource_id: accountResource.resource!.id,
                                    unit_price: accountResource.unit_price,
                                    notes: accountResource.notes || '',
                                };
                            },
                        ),
                    },
                },
            });
            onSuccess();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <RhFormWrapper
                validationSchema={validationSchema}
                initialValues={initialValues}
                disabled={loading || disabled}
                actionBarStart={
                    account?.id ? (
                        <DAuditInfo
                            created_by={account.created_by}
                            updated_by={account.updated_by}
                        />
                    ) : null
                }
                onSubmit={submitAccount}
                onInvalid={async (errors) => {
                    console.error(errors);
                }}
            >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: ['name', 'abbreviation', 'rfc', 'address'],
                        content: (
                            <>
                                <RHTextField
                                    name={'name'}
                                    label={shortLabel(FIELD_LABELS.name)}
                                    onChange={setName}
                                />
                                <RHTextField
                                    name={'abbreviation'}
                                    label={shortLabel(
                                        FIELD_LABELS.abbreviation,
                                    )}
                                />
                                <RHTextField
                                    name={'rfc'}
                                    label={shortLabel(FIELD_LABELS.rfc)}
                                />
                                <RHTextField
                                    name={'address'}
                                    label={shortLabel(FIELD_LABELS.address)}
                                />
                                <SimilarAccountNameWarning
                                    matches={similarNameMatches}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Contactos',
                        optional: true,
                        fields: ['account_contacts'],
                        content: <AccountContactsColumns />,
                    },
                    {
                        label: 'Configuración',
                        optional: true,
                        fields: [
                            IS_CLIENT,
                            IS_SUPPLIER,
                            IS_INFORMAL_ACCOUNT,
                            EXCLUDE_FROM_BALANCES_SUMMARY,
                        ],
                        content: (
                            <>
                                <RhCheckbox
                                    name={IS_CLIENT}
                                    label={shortLabel(FIELD_LABELS.is_client)}
                                />
                                <RhCheckbox
                                    name={IS_SUPPLIER}
                                    label={shortLabel(FIELD_LABELS.is_supplier)}
                                />
                                {/* Read-only: only "Inopack Notas" is informal,
                                    seeded in the DB. Shown disabled for context. */}
                                <RhCheckbox
                                    name={IS_INFORMAL_ACCOUNT}
                                    disabled
                                    label={shortLabel(
                                        FIELD_LABELS.is_informal_account,
                                    )}
                                />
                                {/* Read-only: seeded on Notas + Efectivo. Hides the
                                    account from the Saldos y Pagos / Transfers
                                    ledger and the Acumulado. Shown for context. */}
                                <RhCheckbox
                                    name={EXCLUDE_FROM_BALANCES_SUMMARY}
                                    disabled
                                    label={shortLabel(
                                        FIELD_LABELS.exclude_from_balances_summary,
                                    )}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Cliente',
                        optional: true,
                        fields: [
                            'requires_order_request',
                            'client_credit_days',
                            'client_require_credit_note',
                            'client_require_supplement',
                            'client_requires_invoice_code',
                            'client_requires_tax',
                            'client_automatic_tax_calculation',
                            'client_reconciliation_only',
                            ACCOUNT_PRODUCTS,
                        ],
                        // Client-only settings: the order-request requirement, the
                        // credit terms/defaults that pre-fill order sales, and the
                        // product catalog. Only relevant when this account is a
                        // client; the predicate re-runs whenever `is_client` flips.
                        disabled: (values) => !values[IS_CLIENT],
                        content: (
                            <>
                                <FormFieldSection title={'Condiciones de venta'}>
                                    <RequiresOrderRequest />
                                    <RhNumberField
                                        name={'client_credit_days'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_credit_days,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Documentación'}>
                                    <RhCheckbox
                                        name={'client_require_credit_note'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_require_credit_note,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={'client_require_supplement'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_require_supplement,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={'client_requires_invoice_code'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_requires_invoice_code,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Impuestos y conciliación'}>
                                    <RhCheckbox
                                        name={'client_requires_tax'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_requires_tax,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={'client_automatic_tax_calculation'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_automatic_tax_calculation,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={'client_reconciliation_only'}
                                        label={shortLabel(
                                            FIELD_LABELS.client_reconciliation_only,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Catálogo de productos'}>
                                    <AccountProductsColumns products={products} />
                                </FormFieldSection>
                            </>
                        ),
                    },
                    {
                        label: 'Proveedor',
                        optional: true,
                        fields: [
                            'supplier_credit_days',
                            'supplier_require_supplement',
                            'supplier_requires_external_code',
                            'supplier_requires_tax',
                            'supplier_reconciliation_only',
                            'supplier_is_draft',
                            'resource_id',
                            ACCOUNT_RESOURCES,
                            'supplier_recurring_expenses',
                            MONITOR_SUPPLIER_EXPENSES,
                        ],
                        // Supplier-only defaults that pre-fill expenses, plus the
                        // resource catalog. Only relevant when this account is a
                        // supplier; the predicate re-runs whenever `is_supplier`
                        // flips.
                        disabled: (values) => !values[IS_SUPPLIER],
                        content: (
                            <>
                                <FormFieldSection title={'Condiciones de compra'}>
                                    <RhNumberField
                                        name={'supplier_credit_days'}
                                        label={shortLabel(
                                            FIELD_LABELS.supplier_credit_days,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Documentación'}>
                                    <RhCheckbox
                                        name={'supplier_require_supplement'}
                                        label={shortLabel(
                                            FIELD_LABELS.supplier_require_supplement,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={'supplier_requires_external_code'}
                                        label={shortLabel(
                                            FIELD_LABELS.supplier_requires_external_code,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Impuestos y conciliación'}>
                                    <RhCheckbox
                                        name={'supplier_requires_tax'}
                                        label={shortLabel(
                                            FIELD_LABELS.supplier_requires_tax,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={'supplier_reconciliation_only'}
                                        label={shortLabel(
                                            FIELD_LABELS.supplier_reconciliation_only,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Catálogo de recursos'}>
                                    <ResourceIdInput />
                                    <AccountResourcesColumns
                                        resources={resources}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Resúmenes'}>
                                    <RhCheckbox
                                        name={'supplier_recurring_expenses'}
                                        label={shortLabel(
                                            FIELD_LABELS.supplier_recurring_expenses,
                                        )}
                                    />
                                    <RhCheckbox
                                        name={MONITOR_SUPPLIER_EXPENSES}
                                        label={shortLabel(
                                            FIELD_LABELS.monitor_supplier_expenses,
                                        )}
                                    />
                                </FormFieldSection>
                                <FormFieldSection title={'Borrador'}>
                                    <SupplierDraftDefault />
                                </FormFieldSection>
                            </>
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
}
